import React, { useEffect, useState } from 'react';
import { Check, ContentCopy } from '@mui/icons-material';
import {
  Link, styled, TextField, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNotification } from 'shared/hooks';
import { copyValueToClipboard } from 'shared/utils';
import StyledDataDisplay from 'shared/components/styled/info';
import { ValueDisplay } from 'shared/components/styled/layouts';

const CopyableBox = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'copyable', // Prevent `copyable` from being forwarded
})(({ theme, copyable }) => ({
  position: 'relative',
  margin: '10px 0',
  color: (theme.vars || theme).palette.text.primary,
  width: '100%',

  ...(copyable && {
    cursor: 'pointer',

    '&:hover': {
      color: (theme.vars || theme).palette.primary.main,
      cursor: 'pointer',

      '& .data-display-contents': {
        background: (theme.vars || theme).palette.primaryHover,
      },

      '& svg': {
        opacity: 1,
        transition: 'opacity 195ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        visibility: 'visible', // Make the SVG interactive
      },
    },

    '& svg': {
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 195ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 195ms',
      margin: '8.5px',
      height: 20,
    },
  }),
}));

export const StyledCopyableTextarea = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'copyable',
})(({ theme, copyable }) => ({
  ...(copyable && {

    '& .MuiInputBase-root': {
      paddingRight: 0,
    },

    '&:hover .MuiInputBase-root': {
      cursor: 'pointer',
      color: (theme.vars || theme).palette.primary.main,
      backgroundColor: (theme.vars || theme).palette.primaryHover,
    },
    '&:hover .MuiInputBase-input': {
      cursor: 'pointer !important',
    },
    '& svg': {
      opacity: 0,
      visibility: 'hidden',
      transition: 'opacity 195ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 195ms',
      margin: '8.5px',
      height: 20,
    },
  }),
}));

/**
 * @param {Object} props
 * @param {string} props.label
 * @param {Object} [props.action]
 * @param {string|number|Object} [props.value=' ']
 * @param {boolean} [props.copyable=false]
 * @param {boolean} [props.multiline=false]
 */
export default function DataDisplay({
  value = ' ',
  label,
  action,
  copyable = false,
  multiline = false,
}) {
  const notification = useNotification();
  const [isURL, setIsURL] = useState(false);

  const [icon, setIcon] = useState(<ContentCopy />);

  useEffect(() => {
    try {
      const url = new URL(value);
      setIsURL(url.host !== '');
    } catch (err) {
      setIsURL(false);
    }
  }, [value]);

  const handleClick = async () => {
    if (!copyable) return;
    // supports copying content from a React node (legacy notes)
    if (typeof value === 'object') await copyValueToClipboard(value?.props?.content);
    else await copyValueToClipboard(value);
    notification.pushSuccess(`copied ${label} to clipboard`);
    setIcon(<Check />);
    window.setTimeout(() => {
      setIcon(<ContentCopy />);
    }, 2000);
  };

  const display = (v) => (
    <StyledDataDisplay>
      <Grid className="label">{label}</Grid>
      <Grid container className="data-display-contents" sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid size={10}>
          <ValueDisplay>
            {isURL ? (
              <Link
                onClick={(e) => e.stopPropagation()}
                href={value}
                target="_blank"
                rel="noopener"
              >
                {v}
              </Link>
            ) : (
              <Typography>{ v || ' ' }</Typography>
            )}
          </ValueDisplay>
        </Grid>
        <Grid size="auto" sx={{ justifyContent: 'end', alignContent: 'center' }}>
          {action && (
            <div>
              {action}
            </div>
          )}
          { copyable && (
          <span>{ icon }</span>
          )}
        </Grid>
      </Grid>
      <fieldset>
        <legend>
          <span>{label}</span>
        </legend>
      </fieldset>
    </StyledDataDisplay>
  );

  // `readOnly`, not `disabled`: keeps the text selectable and full-contrast.
  const area = (v) => (
    <StyledCopyableTextarea
      value={v.props.content}
      multiline
      label="Legacy Notes"
      slotProps={{ input: { endAdornment: icon, readOnly: true } }}
      fullWidth
      copyable
    />
  );

  return (
    <CopyableBox
      copyable={copyable}
      onClick={handleClick}
    >
      {(multiline && value.props?.content) ? (
        area(value)
      ) : (
        display(value)
      )}
    </CopyableBox>
  );
}
